import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toTotpCode } from '../api/mappers';
import { useRegenerateRecoveryCodes } from '../hooks/use-two-factor-mutations';
import { twoFactorFieldErrors } from '../schemas/two-factor-rules';
import { authenticatorCodeSchema, type CodeValues } from '../schemas/two-factor-schemas';

import { OneTimeCodeInput } from './one-time-code-input';
import { RecoveryCodesPanel } from './recovery-codes-panel';

/** Replaces all recovery codes after a live authenticator code; the old ones stop working. */
export function RegenerateRecoveryCodesDialog({ account }: { account: string }) {
  const { t } = useTranslation('auth');
  const message = useErrorMessage();
  const regenerate = useRegenerateRecoveryCodes();
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const form = useForm<CodeValues>({
    resolver: useI18nResolver(authenticatorCodeSchema),
    defaultValues: { code: '' },
  });

  const onOpenChange = (next: boolean) => {
    if (!next && codes) return;
    setOpen(next);
    if (!next) {
      form.reset();
      regenerate.reset();
    }
  };

  const finish = () => {
    setCodes(null);
    setOpen(false);
    form.reset();
    regenerate.reset();
    toast.success(t('security.regeneratedToast'));
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const result = await regenerate.mutateAsync({ code: toTotpCode(values.code) });
      setCodes(result.recoveryCodes);
    } catch (error) {
      if (twoFactorFieldErrors(error).includes('code')) {
        form.setError(
          'code',
          { type: 'server', message: t('setup.codeMismatch') },
          { shouldFocus: true },
        );
      } else {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  const rootError = form.formState.errors.root?.server?.message;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <KeyRound aria-hidden />
          {t('security.regenerate')}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg"
        showCloseButton={!codes}
      >
        <DialogHeader>
          <DialogTitle>{codes ? t('recoveryCodes.title') : t('regenerate.title')}</DialogTitle>
          <DialogDescription>
            {codes ? t('recoveryCodes.description') : t('regenerate.description')}
          </DialogDescription>
        </DialogHeader>
        {codes ? (
          <RecoveryCodesPanel codes={codes} account={account} onDone={finish} />
        ) : (
          <Form {...form}>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              {rootError ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{rootError}</AlertDescription>
                </Alert>
              ) : null}
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('twoFactor.label.totp')}</FormLabel>
                    <FormControl>
                      <OneTimeCodeInput autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit" disabled={regenerate.isPending}>
                  {regenerate.isPending ? t('regenerate.submitting') : t('regenerate.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
